/**
 * Commentary takes (C10): a bundle's commentary.json ships take 0 in `lines`, the lines only other
 * takes play in `extra`, and each take's `[id, t_pres]` schedule in `takes`. The player never
 * schedules: `takeOf` turns a take index back into a plain CommentaryFile whose `lines` are that
 * take, so the CommentaryDriver and the captions keep reading `file.lines`. `pickTake` is the
 * START gesture's "different from last time" draw, and the localStorage helpers remember the last
 * take this viewer heard (every access wrapped so a blocked storage fails safe).
 */
import type { CommentaryFile, CommentaryLine, CommentaryPoolLine } from '../bundle/manifest.gen.ts';

/** How many takes a commentary file ships: `takes.length`, or 1 for an older bundle without `takes`. */
export function takeCount(file: CommentaryFile | null): number {
  return file?.takes?.length ?? 1;
}

/**
 * Take k of a commentary file: every `[id, t_pres]` of `takes[k]` looked up in `lines` ∪ `extra`
 * (t_pres from the take, not the line's own), sorted by t_pres. A file without `takes`, or k out
 * of range or not an integer, comes back unchanged: an older bundle has exactly one take.
 */
export function takeOf(file: CommentaryFile | null, k: number): CommentaryFile | null {
  const takes = file?.takes;
  if (!file || takes === undefined) return file;
  if (!Number.isInteger(k) || k < 0 || k >= takes.length) return file;
  const pool = new Map<string, CommentaryLine | CommentaryPoolLine>();
  for (const l of file.lines) pool.set(l.id, l);
  for (const l of file.extra ?? []) pool.set(l.id, l);
  const lines: CommentaryLine[] = [];
  for (const [id, tPres] of takes[k] ?? []) {
    const l = pool.get(id);
    if (l) lines.push({ ...l, t_pres: tPres });
  }
  lines.sort((a, b) => a.t_pres - b.t_pres);
  return { ...file, lines };
}

/** localStorage key for the last take this viewer heard of a race (C10 "different from last time"). */
export function lastTakeKey(raceKey: string): string {
  return `undercut.take.${raceKey}`;
}

/** The last take stored for the race, or null when nothing readable is stored. */
export function readLastTake(raceKey: string, storage?: Pick<Storage, 'getItem'>): number | null {
  try {
    const v = (storage ?? localStorage).getItem(lastTakeKey(raceKey)); // inside the try: reading `localStorage` itself can throw
    if (v === null || v.trim() === '') return null; // Number(null) would read as take 0
    const n = Number(v);
    return Number.isInteger(n) && n >= 0 ? n : null;
  } catch {
    return null; // a blocked or throwing storage is not an error
  }
}

/** Remember a take (best effort; a throwing storage is ignored). */
export function writeLastTake(raceKey: string, take: number, storage?: Pick<Storage, 'setItem'>): void {
  try {
    (storage ?? localStorage).setItem(lastTakeKey(raceKey), String(take));
  } catch {
    // fail safe: the next playback just may repeat a take
  }
}

/**
 * A random 0-based take index in [0, k), never `last` when k > 1. `random` is injected so tests
 * can pin the draw; the player passes Math.random — the pick lands in the URL hash, so the take
 * stays replayable from there.
 */
export function pickTake(k: number, last: number | null, random: () => number): number {
  if (k <= 1) return 0;
  const avoid = last !== null && last >= 0 && last < k;
  const i = Math.floor(random() * (avoid ? k - 1 : k));
  return avoid && i === last ? k - 1 : i;
}
